'use client';
import { PromotionStar as PromotionStarIcon } from '@ahafood/icons';
import { Badge } from '@ahafood/ui/components/badge';
import { Card } from '@ahafood/ui/components/card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { BadgeCheck } from 'lucide-react';
import {
  ETABadge,
  RatingBadge,
} from '../../(main)/store/[storeSlug]/components/store-header';
import HighlightItems from '../../(main)/store/components/highlight-items';
import ImageWithFallback from '../../(main)/store/components/items/fallback-image';
import AiSummaryHighlight from '../../cua-hang/components/ai-summary-highlight';
import type { Store } from '@/api/ahafood';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

const STORE_FALLBACK_IMAGE = `${assetPrefix}/static/images/empty-store.webp`;

const InactiveBadge = () => (
  <div className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-50/60 rounded-2xl">
    <Badge className="bg-neutral-90 hover:bg-neutral-90 px-2.5 py-1 shadow-md">
      <span className="text-xs font-bold text-white">Đóng cửa</span>
    </Badge>
  </div>
);

type Props = {
  data: Store;
  className?: string;
  position?: number;
};

export default function StoreCard({ data, className, position }: Props) {
  const { user } = useAuthStore();
  const router = useRouter();
  const routes = useCityRoutes();
  const isClosed = data.is_temporary_closed;

  const handleCardClick = () => {
    if (isClosed) return;
    trackEvent('click_item_card', {
      action_time: Math.floor(Date.now() / 1000),
      item_type: 'store_item',
      position: position ?? 0,
      distance: data.distance,
      total_sold: data.total_sold,
      rating: data.rating.average,
      address: data.nearest_branch?.address || data.address,
      store_id: data._id,
      store_name: data.name,
      user_id: user?.cunstomerId,
    });
    if (data.slug) {
      router.push(routes.storeDetail(data.slug));
    }
  };

  const hasHighlightItems = Boolean(data.highlight_items?.length);
  const hasAiSummary = Boolean(data.summary_review?.highlights);

  return (
    <Card
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (isClosed) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role="button"
      tabIndex={isClosed ? -1 : 0}
      className={cn(
        // Strip Card defaults — no border/shadow/bg, transparent row
        'border-0 shadow-none bg-transparent rounded-none gap-2 overflow-hidden',
        'flex flex-col py-1',
        isClosed
          ? 'pointer-events-none opacity-90'
          : 'cursor-pointer [@media(hover:hover)]:hover:opacity-90',
        className
      )}
    >
      {/* Row chính: image trái + content phải */}
      <div className="flex flex-row gap-3">
        <div className="relative shrink-0 size-28 overflow-hidden rounded-2xl bg-neutral-10">
          {isClosed && <InactiveBadge />}
          <ImageWithFallback
            src={data.image_url ?? STORE_FALLBACK_IMAGE}
            alt={data.name}
            fill
            sizes="112px"
            style={{ objectFit: 'cover' }}
            fallbackSrc={STORE_FALLBACK_IMAGE}
          />
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {/* Name + verified badge */}
          <div className="flex items-center gap-1.5 min-w-0">
            <h3 className="flex-1 min-w-0 text-sm sm:text-base font-semibold text-neutral-90 truncate">
              {data.name}
            </h3>
            {data.verified ? (
              <BadgeCheck
                className="shrink-0 size-4 fill-yellow-40 text-white"
                aria-label="Đã xác thực"
              />
            ) : null}
          </div>

          <RatingBadge
            className="border-none p-0"
            rating={data.rating}
            total_sold={data.total_sold}
          />

          <ETABadge cityId={data.city_id} distance={data.distance} />

          {/* AI summary — text-only, gradient bg block */}
          {hasAiSummary ? (
            <AiSummaryHighlight summary={data.summary_review} />
          ) : null}

          {/* Voucher chips */}
          {data.ai_tags && data.ai_tags.length > 0 ? (
            <div
              role="presentation"
              className="overflow-x-auto scrollbar-hide touch-pan-x"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="flex gap-1.5">
                {data.ai_tags.map((tag, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="shrink-0 bg-primary-10 border-primary-20 px-2 py-0.5 text-[11px] font-semibold text-primary-50 whitespace-nowrap"
                  >
                    <PromotionStarIcon className="size-3 mr-1 fill-primary-40 text-primary-40" />
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Highlight items — full width dưới row (desktop only, component tự handle md:block) */}
      {hasHighlightItems && !isClosed ? (
        <HighlightItems items={data.highlight_items ?? []} />
      ) : null}
    </Card>
  );
}
